import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Lock, ArrowRight, CheckCircle2, Eye, EyeOff } from "lucide-react";
import SmartLogo from "../components/SmartLogo";

const formatErr = (d) => {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex bg-[#FDFDFD] px-6 sm:px-12 py-10" data-testid="reset-password-page">
      <div className="w-full max-w-md mx-auto">
        <div className="mb-12">
          <SmartLogo />
        </div>

        {done ? (
          <div className="border border-green-200 bg-green-50 p-6 rounded-sm" data-testid="reset-success">
            <CheckCircle2 size={20} className="text-green-700 mb-2" />
            <h2 className="font-display text-xl font-bold tracking-tight mb-1">Password updated</h2>
            <p className="text-sm text-zinc-700">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <span className="label-eyebrow">New password</span>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-3 leading-[1.05]">
              Set a new password.
            </h1>
            <p className="text-sm text-zinc-600 mt-2">Choose something at least 8 characters long.</p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
              <PwField label="New password" value={password} onChange={setPassword} testId="reset-password" />
              <PwField label="Confirm password" value={confirm} onChange={setConfirm} testId="reset-confirm" />
              <button type="submit" disabled={busy || !token} data-testid="reset-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-zinc-950 text-white font-semibold px-5 py-3 rounded-sm hover:bg-zinc-800 disabled:opacity-50">
                {busy ? "Updating…" : "Update password"} <ArrowRight size={16} />
              </button>
              <Link to="/login" className="block text-sm text-center text-zinc-600 hover:text-zinc-950 underline pt-2">
                Back to sign in
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const PwField = ({ label, value, onChange, testId }) => {
  const [showPw, setShowPw] = useState(false);
  return (
    <div>
      <label className="label-eyebrow block mb-1.5">{label}</label>
      <div className="relative">
        <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          type={showPw ? "text" : "password"} required value={value}
          onChange={e => onChange(e.target.value)} placeholder="••••••••"
          data-testid={testId}
          className="w-full pl-9 pr-9 py-2.5 border border-zinc-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-1"
        />
        <button
          type="button"
          onClick={() => setShowPw(v => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors"
          tabIndex={-1}
          aria-label={showPw ? "Hide password" : "Show password"}
        >
          {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
};
